'use client'

import { Check, Loader2 } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/auth-context'
import { ANALYTICS_EVENTS, capture } from '@/lib/analytics'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

/**
 * First-run developer onboarding survey for the dojo cabinet.
 *
 * Shows a one-shot, full-screen prompt the first time an authenticated user
 * reaches the cabinet (gated on `UserPreference.onboardingCompletedAt == null`),
 * asking their role + what they plan to use Extenshi for. It is SKIPPABLE —
 * skipping still stamps `onboardingCompletedAt`, so the prompt never nags twice.
 *
 * Consent is intentionally NOT part of this flow: Terms/Privacy acceptance is
 * captured at genkan signup (click-wrap), so re-prompting here would be
 * redundant double-consent.
 *
 * It is mounted inside AppChrome's full-chrome branch, so it already never
 * renders on the bare public routes (`/welcome`, `/uninstall-form`). We
 * additionally suppress it on `/extensions/*` — that is the post-claim /
 * ownership-proof landing, and the survey must not interrupt that flow.
 */

const ROLE_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
	{ key: 'developer', label: 'Extension developer / engineer' },
	{ key: 'founder', label: 'Founder / indie hacker' },
	{ key: 'agency', label: 'Agency / freelancer' },
	{ key: 'security', label: 'Security / privacy researcher' },
	{ key: 'product', label: 'Product / marketing / growth' },
	{ key: 'student', label: 'Student / hobbyist' },
	{ key: 'other', label: 'Other' },
]

const USE_CASE_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
	{ key: 'publish', label: 'Publish & manage my extensions' },
	{ key: 'scanning', label: 'Pre-publish security scanning' },
	{ key: 'research', label: 'Competitive / market research' },
	{ key: 'analytics', label: 'Reviews & analytics' },
	{ key: 'devtools', label: 'Dev tools (privacy policy / manifest)' },
	{ key: 'exploring', label: 'Just exploring' },
	{ key: 'other', label: 'Other' },
]

// Routes where the survey must never appear. AppChrome already withholds the
// full chrome (and therefore this gate) on `/welcome` + `/uninstall-form`;
// `/extensions` is the ownership-proof / post-claim deep-link path.
const EXCLUDED_PREFIXES = ['/extensions', '/welcome', '/uninstall-form']

interface SurveyInput {
	role?: string
	useCases?: string[]
	skipped?: boolean
}

export function OnboardingSurveyGate() {
	const { isAuthenticated } = useAuth()
	const pathname = usePathname() ?? ''
	const excluded = EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p))
	const enabled = isAuthenticated && !excluded

	const utils = trpc.useUtils()
	const { data: preferences } = trpc.user.getPreferences.useQuery(undefined, { enabled })

	const [dismissed, setDismissed] = useState(false)
	const [role, setRole] = useState<string | null>(null)
	const [useCases, setUseCases] = useState<string[]>([])

	const submit = trpc.user.submitOnboardingSurvey.useMutation({
		onSuccess: (_data: unknown, variables: SurveyInput) => {
			// Completion means persisted, including the skipped preference.
			capture(
				variables.skipped
					? ANALYTICS_EVENTS.ONBOARDING_SURVEY_SKIPPED
					: ANALYTICS_EVENTS.ONBOARDING_SURVEY_COMPLETED,
				variables.skipped ? {} : { role: variables.role, use_cases: variables.useCases },
			)
			// Hide immediately, then refresh the cached preference so the gate
			// stays closed across navigations without waiting on the refetch.
			setDismissed(true)
			utils.user.getPreferences.invalidate()
		},
		onError: (error: { data?: { code?: string } | null }, variables: SurveyInput) => {
			capture(ANALYTICS_EVENTS.ONBOARDING_SURVEY_FAILED, {
				action: variables.skipped ? 'skip' : 'submit',
				error_code: error.data?.code ?? 'UNKNOWN',
			})
		},
	})

	const show = enabled && !dismissed && !!preferences && preferences.onboardingCompletedAt == null
	if (!show) return null

	const toggleUseCase = (key: string) => {
		setUseCases((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
	}

	const handleSubmit = () => {
		submit.mutate({ role: role ?? undefined, useCases, skipped: false })
	}

	const handleSkip = () => {
		submit.mutate({ skipped: true })
	}

	const busy = submit.isPending

	return (
		<div className="fixed inset-0 z-[60] grid place-items-center bg-background/80 p-4 backdrop-blur-sm">
			<div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-lg">
				<h2 className="text-xl font-semibold">Welcome to Extenshi</h2>
				<p className="mt-1 text-sm text-muted-foreground">
					Two quick questions so we can tailor things to you. You can skip this.
				</p>

				<div className="mt-6 space-y-2">
					<p className="text-sm font-medium">What best describes you?</p>
					<p className="text-xs text-muted-foreground">Select one to continue</p>
					<div className="flex flex-wrap gap-2">
						{ROLE_OPTIONS.map((opt) => {
							const selected = role === opt.key
							return (
								<button
									key={opt.key}
									type="button"
									disabled={busy}
									onClick={() => setRole(opt.key)}
									className={cn(
										'rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-50',
										selected
											? 'border-primary bg-primary text-primary-foreground'
											: 'border-input bg-background hover:bg-accent hover:text-accent-foreground',
									)}
								>
									{opt.label}
								</button>
							)
						})}
					</div>
				</div>

				<div className="mt-6 space-y-2">
					<p className="text-sm font-medium">What do you plan to use Extenshi for?</p>
					<p className="text-xs text-muted-foreground">Select all that apply</p>
					<div className="flex flex-wrap gap-2">
						{USE_CASE_OPTIONS.map((opt) => {
							const selected = useCases.includes(opt.key)
							return (
								<button
									key={opt.key}
									type="button"
									disabled={busy}
									onClick={() => toggleUseCase(opt.key)}
									className={cn(
										'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-50',
										selected
											? 'border-primary bg-primary text-primary-foreground'
											: 'border-input bg-background hover:bg-accent hover:text-accent-foreground',
									)}
								>
									{selected && <Check className="h-3.5 w-3.5" />}
									{opt.label}
								</button>
							)
						})}
					</div>
				</div>

				<div className="mt-8 flex items-center justify-between gap-3">
					<Button variant="ghost" onClick={handleSkip} disabled={busy}>
						Skip for now
					</Button>
					<Button onClick={handleSubmit} disabled={busy || role === null}>
						{busy ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Saving…
							</>
						) : (
							'Continue'
						)}
					</Button>
				</div>
			</div>
		</div>
	)
}
